
import React, { useState, useEffect } from 'react';
import {
  Settings,
  ArrowLeft,
  User as UserIcon,
  Mic,
  Bell,
  CreditCard,
  Shield,
  AlertTriangle,
  ExternalLink,
  Download,
  Trash2,
  Check,
  X,
} from 'lucide-react';
import { getSettings, saveSettings, AppSettings } from '../services/jobService';
import { supabase, auth, onAuthStateChanged } from '../services/supabase';
import type { User } from '../services/supabase';
import DeleteAccountModal from './DeleteAccountModal';

interface SettingsPageProps {
  onBack: () => void;
}

type SectionId = 'account' | 'transcription-engine' | 'notifications' | 'billing' | 'data-privacy' | 'danger-zone';

const Toggle: React.FC<{ checked: boolean; onChange: (v: boolean) => void; label: string; description?: string }> = ({
  checked,
  onChange,
  label,
  description,
}) => (
  <div className="flex items-center justify-between py-3 border-t border-base-200 dark:border-dark-300">
    <div>
      <p className="text-sm font-semibold text-base-content dark:text-dark-content">{label}</p>
      {description && (
        <p className="text-xs text-base-content/60 dark:text-dark-content/60 mt-0.5">{description}</p>
      )}
    </div>
    <button
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-offset-2 ${
        checked ? 'bg-brand-primary' : 'bg-base-300 dark:bg-dark-300'
      }`}
    >
      <span
        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
          checked ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
    </button>
  </div>
);

const SettingsPage: React.FC<SettingsPageProps> = ({ onBack }) => {
  const [activeSection, setActiveSection] = useState<SectionId>('account');
  const [settings, setSettings] = useState<AppSettings>(getSettings);
  const [user, setUser] = useState<User | null>(null);
  const [notifPermission, setNotifPermission] = useState<NotificationPermission>('default');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setUser(u));
    return () => unsub();
  }, []);

  useEffect(() => {
    if ('Notification' in window) setNotifPermission(Notification.permission);
  }, []);

  const updateSetting = <K extends keyof AppSettings>(key: K, value: AppSettings[K]) => {
    const updated = { ...settings, [key]: value };
    setSettings(updated);
    saveSettings(updated);
  };

  const requestNotificationPermission = async () => {
    if (!('Notification' in window)) return;
    const permission = await Notification.requestPermission();
    setNotifPermission(permission);
  };

  const handleExportData = async () => {
    if (!user) return;
    setExportLoading(true);
    try {
      const { data: transcripts, error } = await supabase
        .from('transcripts')
        .select('*')
        .eq('user_id', user.id);
      if (error) throw error;
      const exportPayload = {
        exportedAt: new Date().toISOString(),
        user: { id: user.id, email: user.email, displayName: user.user_metadata?.full_name || user.user_metadata?.name },
        transcripts,
      };
      const blob = new Blob([JSON.stringify(exportPayload, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `echoscribe-export-${Date.now()}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Export failed', e);
    } finally {
      setExportLoading(false);
    }
  };

  const sections: { id: SectionId; label: string; icon: React.ReactNode }[] = [
    { id: 'account',             label: 'Account',              icon: <UserIcon size={15} /> },
    { id: 'transcription-engine',label: 'Transcription',        icon: <Mic size={15} /> },
    { id: 'notifications',       label: 'Notifications',        icon: <Bell size={15} /> },
    { id: 'billing',             label: 'Billing',              icon: <CreditCard size={15} /> },
    { id: 'data-privacy',        label: 'Data & Privacy',       icon: <Shield size={15} /> },
    { id: 'danger-zone',         label: 'Danger Zone',          icon: <AlertTriangle size={15} /> },
  ];

  if (deleteSuccess) {
    return (
      <div className="w-full max-w-2xl mx-auto px-4 pb-12 flex flex-col items-center justify-center py-24 animate-fade-in">
        <div className="w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center text-green-500 mb-6">
          <Check size={32} />
        </div>
        <h2 className="text-2xl font-bold text-base-content dark:text-dark-content mb-2">Account Deleted</h2>
        <p className="text-base-content/60 dark:text-dark-content/60 text-center">
          Your account and all associated data have been permanently deleted.
        </p>
      </div>
    );
  }

  return (
    <div className="w-full max-w-5xl mx-auto px-4 pb-12 animate-fade-in">
      {/* Top bar */}
      <div className="flex items-center gap-3 mb-8">
        <button
          onClick={onBack}
          className="p-2 rounded-xl text-base-content/60 dark:text-dark-content/60 hover:bg-base-200 dark:hover:bg-dark-300 transition-colors"
          aria-label="Back"
        >
          <ArrowLeft size={20} />
        </button>
        <div className="flex items-center gap-2">
          <Settings className="w-5 h-5 text-brand-primary" />
          <h1 className="text-xl font-bold text-base-content dark:text-dark-content">Settings</h1>
        </div>
      </div>

      <div className="flex gap-6">
        {/* Sidebar */}
        <aside className="w-52 flex-shrink-0">
          <nav className="space-y-0.5">
            {sections.map((s) => {
              const isActive = activeSection === s.id;
              const isDanger = s.id === 'danger-zone';
              return (
                <button
                  key={s.id}
                  onClick={() => setActiveSection(s.id)}
                  className={`w-full text-left px-3 py-2.5 rounded-xl text-sm font-medium flex items-center gap-2.5 transition-all duration-150 ${
                    isActive
                      ? isDanger
                        ? 'bg-red-500/10 text-red-500'
                        : 'bg-brand-primary/10 text-brand-primary'
                      : isDanger
                        ? 'text-red-400/70 hover:bg-red-500/10 hover:text-red-500'
                        : 'text-base-content/60 dark:text-dark-content/60 hover:bg-base-200 dark:hover:bg-dark-300 hover:text-base-content dark:hover:text-dark-content'
                  }`}
                >
                  {s.icon}
                  {s.label}
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Panel */}
        <div className="flex-1 min-w-0">

          {/* ── Account ── */}
          {activeSection === 'account' && (
            <section className="bg-white/50 dark:bg-dark-200/50 backdrop-blur-sm border border-base-200 dark:border-dark-300 rounded-2xl p-6 animate-fade-in">
              <h2 className="text-sm font-bold uppercase tracking-wider text-base-content/60 dark:text-dark-content/60 mb-4 flex items-center gap-2">
                <UserIcon size={14} /> Account
              </h2>
              {user ? (
                <div className="flex items-center gap-4">
                  {user.user_metadata?.avatar_url ? (
                    <img src={user.user_metadata.avatar_url} alt={user.user_metadata?.full_name || 'User'} className="w-14 h-14 rounded-full border border-base-200 dark:border-dark-300 flex-shrink-0" />
                  ) : (
                    <div className="w-14 h-14 rounded-full bg-brand-primary flex items-center justify-center text-white text-xl font-bold flex-shrink-0">
                      {user.email?.[0].toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0">
                    {(user.user_metadata?.full_name || user.user_metadata?.name) && (
                      <p className="font-bold text-base-content dark:text-dark-content truncate">{user.user_metadata?.full_name || user.user_metadata?.name}</p>
                    )}
                    <p className="text-sm text-base-content/60 dark:text-dark-content/60 truncate">{user.email}</p>
                    <p className="text-xs mt-1 inline-flex items-center gap-1 text-base-content/40 dark:text-dark-content/40">
                      <span className={`w-2 h-2 rounded-full ${user.email_confirmed_at ? 'bg-green-500' : 'bg-amber-500'}`} />
                      {user.email_confirmed_at ? 'Verified' : 'Email not verified'}
                    </p>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-base-content/60 dark:text-dark-content/60">Sign in to manage your account settings.</p>
              )}
            </section>
          )}

          {/* ── Transcription Engine ── */}
          {activeSection === 'transcription-engine' && (
            <section className="bg-white/50 dark:bg-dark-200/50 backdrop-blur-sm border border-base-200 dark:border-dark-300 rounded-2xl p-6 animate-fade-in">
              <h2 className="text-sm font-bold uppercase tracking-wider text-base-content/60 dark:text-dark-content/60 mb-4 flex items-center gap-2">
                <Mic size={14} /> Transcription Engine
              </h2>
              <p className="text-sm text-base-content/70 dark:text-dark-content/70 leading-relaxed mb-2">
                When a transcription job fails, EchoScribe automatically retries up to 3 times with
                exponential backoff (5s → 15s → 45s). After all retries are exhausted, it can fall
                back to the alternate engine (Gemini ↔ Whisper).
              </p>
              <Toggle
                checked={settings.disableFallback}
                onChange={(v) => updateSetting('disableFallback', v)}
                label="Never fall back to alternate engine"
                description="If enabled, jobs will error instead of switching to the other transcription engine."
              />
            </section>
          )}

          {/* ── Notifications ── */}
          {activeSection === 'notifications' && (
            <section className="bg-white/50 dark:bg-dark-200/50 backdrop-blur-sm border border-base-200 dark:border-dark-300 rounded-2xl p-6 animate-fade-in">
              <h2 className="text-sm font-bold uppercase tracking-wider text-base-content/60 dark:text-dark-content/60 mb-4 flex items-center gap-2">
                <Bell size={14} /> Notifications
              </h2>
              <p className="text-sm text-base-content/70 dark:text-dark-content/70 leading-relaxed mb-2">
                Receive browser notifications when background transcription jobs complete.
              </p>
              <div className="flex items-center justify-between py-3 border-t border-base-200 dark:border-dark-300">
                <div>
                  <p className="text-sm font-semibold text-base-content dark:text-dark-content">Browser Notifications</p>
                  <p className="text-xs text-base-content/60 dark:text-dark-content/60 mt-0.5">
                    Current status:{' '}
                    <span className={
                      notifPermission === 'granted' ? 'text-green-500' :
                      notifPermission === 'denied'  ? 'text-red-500'   : 'text-amber-500'
                    }>
                      {notifPermission === 'granted' ? 'Enabled' : notifPermission === 'denied' ? 'Blocked by browser' : 'Not requested'}
                    </span>
                  </p>
                </div>
                {notifPermission !== 'granted' && notifPermission !== 'denied' && (
                  <button
                    onClick={requestNotificationPermission}
                    className="text-sm font-semibold px-4 py-2 rounded-xl bg-brand-primary text-white hover:bg-brand-dark transition-colors"
                  >
                    Enable
                  </button>
                )}
                {notifPermission === 'granted' && (
                  <span className="flex items-center gap-1.5 text-sm text-green-500 font-semibold">
                    <Check size={14} /> Active
                  </span>
                )}
                {notifPermission === 'denied' && (
                  <span className="text-xs text-red-500 font-medium max-w-[140px] text-right">
                    Unblock in browser settings
                  </span>
                )}
              </div>
            </section>
          )}

          {/* ── Billing ── */}
          {activeSection === 'billing' && (
            <section className="bg-white/50 dark:bg-dark-200/50 backdrop-blur-sm border border-base-200 dark:border-dark-300 rounded-2xl p-6 animate-fade-in">
              <h2 className="text-sm font-bold uppercase tracking-wider text-base-content/60 dark:text-dark-content/60 mb-4 flex items-center gap-2">
                <CreditCard size={14} /> Billing
              </h2>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-sm font-semibold text-base-content dark:text-dark-content">Current Plan</p>
                  <p className="text-xs text-base-content/60 dark:text-dark-content/60 mt-0.5">
                    {user ? 'Manage your subscription and receipts' : 'Sign in to view your plan'}
                  </p>
                </div>
                <span className="px-3 py-1 rounded-full text-xs font-bold bg-base-200 dark:bg-dark-300 text-base-content/70 dark:text-dark-content/70 uppercase tracking-wider">
                  Free
                </span>
              </div>
              <div className="space-y-3 border-t border-base-200 dark:border-dark-300 pt-4">
                <a
                  href="https://billing.stripe.com/p/login/test_00000000"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between w-full px-4 py-3 rounded-xl bg-base-100 dark:bg-dark-300 hover:bg-base-200 dark:hover:bg-dark-100 border border-base-200 dark:border-dark-300 transition-colors group"
                >
                  <div className="flex items-center gap-3">
                    <CreditCard size={16} className="text-brand-primary" />
                    <div>
                      <p className="text-sm font-semibold text-base-content dark:text-dark-content">Stripe Customer Portal</p>
                      <p className="text-xs text-base-content/60 dark:text-dark-content/60">View receipts, invoices &amp; billing history</p>
                    </div>
                  </div>
                  <ExternalLink size={14} className="text-base-content/40 dark:text-dark-content/40 group-hover:text-brand-primary transition-colors" />
                </a>
                <div className="flex items-center justify-between px-4 py-3 rounded-xl bg-gradient-to-r from-brand-primary/10 to-brand-secondary/10 border border-brand-primary/20">
                  <div>
                    <p className="text-sm font-bold text-base-content dark:text-dark-content">Upgrade to Lifetime</p>
                    <p className="text-xs text-base-content/60 dark:text-dark-content/60">One-time $49 — unlimited transcription forever</p>
                  </div>
                  <button
                    onClick={() => window.location.href = '/pricing'}
                    className="text-xs font-bold px-4 py-2 rounded-xl bg-brand-primary text-white hover:bg-brand-dark transition-colors whitespace-nowrap"
                  >
                    View Plans
                  </button>
                </div>
              </div>
            </section>
          )}

          {/* ── Data & Privacy ── */}
          {activeSection === 'data-privacy' && (
            <section className="bg-white/50 dark:bg-dark-200/50 backdrop-blur-sm border border-base-200 dark:border-dark-300 rounded-2xl p-6 animate-fade-in">
              <h2 className="text-sm font-bold uppercase tracking-wider text-base-content/60 dark:text-dark-content/60 mb-4 flex items-center gap-2">
                <Shield size={14} /> Data &amp; Privacy
              </h2>
              <p className="text-sm text-base-content/70 dark:text-dark-content/70 leading-relaxed mb-4">
                You own your data. Export a full copy at any time, or permanently delete your account
                and all associated transcripts (GDPR Article 17).
              </p>
              <button
                onClick={handleExportData}
                disabled={!user || exportLoading}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-base-300 dark:border-dark-300 text-sm font-semibold text-base-content dark:text-dark-content hover:bg-base-200 dark:hover:bg-dark-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download size={16} />
                {exportLoading ? 'Exporting…' : 'Export My Data (JSON)'}
              </button>
              <div className="mt-4 p-4 rounded-xl bg-base-100 dark:bg-dark-300 border border-base-200 dark:border-dark-300">
                <p className="text-xs text-base-content/60 dark:text-dark-content/60 leading-relaxed">
                  Your transcripts are stored securely in Supabase. Audio files uploaded to cloud storage
                  are automatically deleted after 7 days for free-tier users. We do not sell your data.
                </p>
              </div>
            </section>
          )}

          {/* ── Danger Zone ── */}
          {activeSection === 'danger-zone' && (
            <section className="bg-white/50 dark:bg-dark-200/50 backdrop-blur-sm border border-red-200 dark:border-red-900/40 rounded-2xl p-6 animate-fade-in">
              <h2 className="text-sm font-bold uppercase tracking-wider text-red-500/70 mb-4 flex items-center gap-2">
                <AlertTriangle size={14} className="text-red-500" /> Danger Zone
              </h2>
              <p className="text-sm text-base-content/70 dark:text-dark-content/70 leading-relaxed mb-4">
                Permanently delete your account and all associated data. This action cannot be undone.
              </p>
              <button
                onClick={() => setShowDeleteModal(true)}
                disabled={!user}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-red-500 text-white text-sm font-bold hover:bg-red-600 transition-colors shadow-lg shadow-red-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Trash2 size={16} />
                Delete Account
              </button>
            </section>
          )}

        </div>
      </div>

      {showDeleteModal && (
        <DeleteAccountModal
          onClose={() => setShowDeleteModal(false)}
          onDeleted={() => { setDeleteSuccess(true); setShowDeleteModal(false); }}
        />
      )}
    </div>
  );
};

export default SettingsPage;
